import React, { useState, useEffect } from "react"
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom"
import { QueryClientProvider } from "@tanstack/react-query"
import { Toaster } from "sonner"
import { queryClient } from "./state/queryClient"
import { DashboardFiltersProvider } from "./state/dashboardFilters"
import Login from "./components/Login"
import AppLayout from "./components/AppLayout"
import OverviewPage from "./pages/OverviewPage"
import WorkoutsPage from "./pages/WorkoutsPage"
import ExercisesPage from "./pages/ExercisesPage"
import NutritionPage from "./pages/NutritionPage"
import SettingsPage from "./pages/SettingsPage"
import AppStorePage from "./pages/AppStorePage"
import api from "./services/api"

// Initialize theme immediately to prevent flash
const initializeTheme = () => {
  const stored = localStorage.getItem("iridium-analytics-theme")
  const theme = stored || "dark" // Default to dark
  if (theme === "dark") {
    document.documentElement.classList.add("dark")
  } else {
    document.documentElement.classList.remove("dark")
  }
  // Set default if not stored
  if (!stored) {
    localStorage.setItem("iridium-analytics-theme", "dark")
  }
}
initializeTheme()

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // On load, try a lightweight authenticated call to see if cookie is present
    // Use AbortController for timeout to prevent indefinite loading states
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 5000) // 5 second timeout

    fetch("/api/v1/admin/ids", { credentials: "include", signal: controller.signal })
      .then((r) => {
        clearTimeout(timeoutId)
        setIsAuthenticated(r.ok)
      })
      .catch((error) => {
        clearTimeout(timeoutId)
        // On timeout or network error, assume not authenticated
        console.error("Auth check failed:", error.name === "AbortError" ? "timeout" : error.message)
        setIsAuthenticated(false)
      })
      .finally(() => setIsLoading(false))

    return () => {
      clearTimeout(timeoutId)
      controller.abort()
    }
  }, [])

  const handleLogin = () => {
    setIsAuthenticated(true)
  }

  const handleLogout = async () => {
    await api.logout()
    setIsAuthenticated(false)
    queryClient.clear()
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return (
      <QueryClientProvider client={queryClient}>
        <Login onLogin={handleLogin} />
        <Toaster position="top-right" richColors />
      </QueryClientProvider>
    )
  }

  return (
    <QueryClientProvider client={queryClient}>
      <DashboardFiltersProvider>
        <BrowserRouter>
          <Routes>
            <Route element={<AppLayout onLogout={handleLogout} />}>
              <Route index element={<OverviewPage />} />
              <Route path="workouts" element={<WorkoutsPage />} />
              <Route path="exercises" element={<ExercisesPage />} />
              <Route path="nutrition" element={<NutritionPage />} />
              <Route path="appstore" element={<AppStorePage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </DashboardFiltersProvider>
    </QueryClientProvider>
  )
}

export default App
